import type {
  CalculationDefinition,
  CalculationFactorApplicability,
  CalculationOperand,
  CalculationOperandValueType,
  CalculationOperator,
  CalculationResultType,
  CustomFieldOwner,
  FrozenCalculationFactorFact,
  QuoteFieldType,
} from './types';

/** Numeric field types are deliberately narrower than the eight enterprise
 * input types. A calculated result can never be text, a date or an enum. */
export const CALCULATION_RESULT_TYPES: readonly CalculationResultType[] = [
  'number',
  'money',
  'percent',
];

export const CALCULATION_OPERATORS: readonly CalculationOperator[] = [
  'add',
  'subtract',
  'multiply',
  'divide',
];

const OPERATOR_LABELS: Record<CalculationOperator, string> = {
  add: '+',
  subtract: '-',
  multiply: '×',
  divide: '÷',
};

const NUMERIC_FIELD_TYPES = new Set<QuoteFieldType>([
  'number',
  'money',
  'percent',
]);

/** These quote fields are discounts rather than general purpose percentages. */
const DISCOUNT_PERCENT_FIELD_KEYS = new Set([
  'item_discount',
  'scheme_discount',
  'overall_discount',
]);

export interface CalculationFieldDescriptor {
  key: string;
  label?: string;
  owner: CustomFieldOwner;
  type: QuoteFieldType;
  enabled?: boolean;
  calculation?: CalculationDefinition;
  factorApplicability?: CalculationFactorApplicability;
}

export interface CalculationValidationContext {
  fields: readonly CalculationFieldDescriptor[];
  /** System calculations are approved compatibility projections. They refer
   * to typed relationship/aggregation inputs rather than enterprise fields. */
  allowSystemCrossOwner?: boolean;
  currentFieldKey?: string;
}

export interface CalculationValidationResult {
  valid: boolean;
  errors: string[];
  dependencies: string[];
}

export interface CalculationEvaluationResult {
  ok: boolean;
  value?: number;
  missingFieldKeys?: string[];
  error?: string;
}

export interface TypedCalculationValue {
  value: number;
  type: CalculationOperandValueType;
}

/** The only runtime context accepted by approved system calculation refs. It
 * is deliberately typed and contains no field-name lookup or fallback. */
export interface SystemCalculationContext {
  rentalPrice?: number;
  quantity?: number;
  currentPricingBase?: number;
  planDiscount?: number;
  itemDiscount?: number;
  dynamicCost?: number;
  /** Frozen manually-maintained device-master unit price. */
  deviceUnitPrice?: number;
  packageItemPrice?: number | null;
  packageRelations?: readonly {
    relationId?: string;
    itemId?: string;
    packageItemPrice: number | null;
    deviceUnitPrice?: number;
    quantity: number;
  }[];
  pricedLines?: readonly {
    itemId?: string;
    relationId?: string;
    pricingBaseAmount: number;
    planDiscount?: number;
    itemDiscount: number;
    quantity: number;
  }[];
  audit?: {
    quoteId: string;
    quoteVersion: number;
    owner: CustomFieldOwner;
    scopeId: string;
    itemId?: string;
    relationId?: string;
  };
}

export interface CalculationEvaluationContext {
  /** Explicit field types come from the unified resolver/definition registry. */
  fieldTypes?: Readonly<Record<string, CalculationOperandValueType>>;
  /** Approved system resolver input; enterprise formulas cannot use it. */
  system?: SystemCalculationContext;
}

export interface CalculationEvaluationWithFactorsResult
  extends CalculationEvaluationResult {
  factorFacts?: FrozenCalculationFactorFact[];
}

export const SYSTEM_CALCULATION_REFS = {
  rentalSubtotal: 'quote-rental-subtotal-v1',
  unitPriceSubtotal: 'quote-unit-price-subtotal-v1',
  packageItemMonthlySubtotal: 'package-item-monthly-subtotal-v1',
  finalDiscountedRelationLine: 'final-discounted-relation-line-v2',
  planProductPrice: 'plan-product-price-v2',
  planProductSubtotal: 'plan-product-subtotal-v2',
  planPackagePrice: 'plan-package-price-v1',
  packageOriginalPrice: 'package-original-price-v1',
  packageDiscountedQuote: 'package-discounted-quote-v2',
  packageDiscountedUnitPrice: 'package-discounted-unit-price-v2',
  packageDiscountedSubtotal: 'package-discounted-subtotal-v2',
  dynamicCostSubtotal: 'dynamic-cost-subtotal-v1',
} as const;

const LEGACY_SYSTEM_CALCULATION_REFS = {
  finalDiscountedRelationLine: 'final-discounted-relation-line-v1',
  planProductPrice: 'plan-product-price-v1',
  planProductSubtotal: 'plan-product-subtotal-v1',
  packageDiscountedQuote: 'package-discounted-quote-v1',
  packageDiscountedUnitPrice: 'package-discounted-unit-price-v1',
  packageDiscountedSubtotal: 'package-discounted-subtotal-v1',
} as const;

type SystemCalculationRef =
  (typeof SYSTEM_CALCULATION_REFS)[keyof typeof SYSTEM_CALCULATION_REFS];

export interface SystemCalculationDefinition extends CalculationDefinition {
  calculationRef: SystemCalculationRef;
  resolverRef: SystemCalculationRef;
  resultFieldKey: string;
  displayName: string;
  displayFormula: string;
}

/** Canonical metadata for the seven approved system calculations. Their
 * resolverRef is the typed implementation consumed by quoteTotals and every
 * projection; operands are only stable input identities for validation and
 * audit, never a second arithmetic implementation. */
export const SYSTEM_CALCULATION_DEFINITIONS: readonly SystemCalculationDefinition[] =
  [
    {
      calculationRef: SYSTEM_CALCULATION_REFS.rentalSubtotal,
      resultFieldKey: 'rental_subtotal',
      displayName: '小计租金（元/月）',
      displayFormula: '租金 × 数量',
      definitionVersion: 'calculation-definition-v3',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'rental_price' },
        { kind: 'field', fieldKey: 'quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.rentalSubtotal,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.unitPriceSubtotal,
      resultFieldKey: 'unit_price_subtotal',
      displayName: '小计单价/月',
      displayFormula: '单价 × 数量',
      definitionVersion: 'calculation-definition-v3',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'device_unit_price' },
        { kind: 'field', fieldKey: 'quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.unitPriceSubtotal,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.planProductPrice,
      resultFieldKey: 'scheme_product_price',
      displayName: '方案价/月',
      displayFormula: '套餐价/月 × 方案折扣',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'package_item_price' },
        { kind: 'field', fieldKey: 'scheme_discount' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.planProductPrice,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.planProductSubtotal,
      resultFieldKey: 'scheme_product_subtotal',
      displayName: '方案价小计/月',
      displayFormula: '方案价/月 × 套餐关系数量',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'scheme_product_price' },
        { kind: 'field', fieldKey: 'package_relation_quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.planProductSubtotal,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.planPackagePrice,
      resultFieldKey: 'scheme_package_price',
      // Kept solely to interpret previously frozen quotations.  Current
      // management and quote pools use scheme_product_subtotal at each scope.
      displayName: '历史方案专属套餐价格（已退役）',
      displayFormula: '历史冻结：Σ（方案专属价格 × 套餐关系数量）',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'scheme_product_price' },
        { kind: 'field', fieldKey: 'package_relation_quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.planPackagePrice,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine,
      resultFieldKey: 'product_final_discount_price',
      displayName: '产品最终折后价',
      displayFormula: '当前计价基数 × 适用方案折扣 × 行折扣 × 数量',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'current_pricing_base' },
        { kind: 'field', fieldKey: 'scheme_discount' },
        { kind: 'field', fieldKey: 'item_discount' },
        { kind: 'field', fieldKey: 'quantity' },
      ],
      operators: ['multiply', 'multiply', 'multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal,
      resultFieldKey: 'package_item_monthly_subtotal',
      displayName: '套餐价小计/月',
      displayFormula: '套餐价/月 × 套餐关系数量',
      definitionVersion: 'calculation-definition-v3',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'package_item_price' },
        { kind: 'field', fieldKey: 'package_relation_quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.packageOriginalPrice,
      resultFieldKey: 'package_original_price_tax13',
      displayName: '历史套餐金额（已退役）',
      displayFormula: 'Σ（套餐单品价 × 套餐关系数量）',
      definitionVersion: 'calculation-definition-v3',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'package_item_price' },
        { kind: 'field', fieldKey: 'package_relation_quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.packageOriginalPrice,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.packageDiscountedQuote,
      resultFieldKey: 'package_discount_quote_tax13',
      displayName: '套餐最终折后价',
      displayFormula: 'Σ（套餐内产品最终折后价）',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [{ kind: 'field', fieldKey: 'product_final_discount_price' }],
      operators: [],
      resolverRef: SYSTEM_CALCULATION_REFS.packageDiscountedQuote,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice,
      resultFieldKey: 'package_discounted_unit_price',
      displayName: '套餐最终折后价',
      displayFormula: '套餐价/月 × 适用方案折扣 × 行折扣',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'package_item_price' },
        { kind: 'field', fieldKey: 'scheme_discount' },
        { kind: 'field', fieldKey: 'item_discount' },
      ],
      operators: ['multiply', 'multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal,
      resultFieldKey: 'package_discounted_subtotal',
      displayName: '套餐最终折后价小计',
      displayFormula: '套餐最终折后价 × 数量',
      definitionVersion: 'calculation-definition-v4',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'package_discounted_unit_price' },
        { kind: 'field', fieldKey: 'quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal,
    },
    {
      calculationRef: SYSTEM_CALCULATION_REFS.dynamicCostSubtotal,
      resultFieldKey: 'dynamic_cost_subtotal',
      displayName: '动态成本小计',
      displayFormula: '动态成本 × 数量',
      definitionVersion: 'calculation-definition-v3',
      owner: 'quote-line',
      resultType: 'money',
      operands: [
        { kind: 'field', fieldKey: 'dynamic_cost' },
        { kind: 'field', fieldKey: 'quantity' },
      ],
      operators: ['multiply'],
      resolverRef: SYSTEM_CALCULATION_REFS.dynamicCostSubtotal,
    },
  ];

export function isNumericFieldType(type: QuoteFieldType): boolean {
  return NUMERIC_FIELD_TYPES.has(type);
}

export function isCalculationResultType(
  type: QuoteFieldType | CalculationResultType,
): type is CalculationResultType {
  return CALCULATION_RESULT_TYPES.includes(type as CalculationResultType);
}

function validConstant(
  operand: Extract<CalculationOperand, { kind: 'constant' }>,
) {
  if (!CALCULATION_RESULT_TYPES.includes(operand.valueType)) return false;
  if (!Number.isFinite(operand.value)) return false;
  if (operand.valueType === 'money' && operand.value < 0) return false;
  if (
    operand.valueType === 'percent' &&
    (operand.value < 0 || operand.value > 100)
  )
    return false;
  return true;
}

function typeOfOperand(
  operand: CalculationOperand,
  fields: Map<string, CalculationFieldDescriptor>,
): CalculationOperandValueType | undefined {
  if (operand.kind === 'constant') return operand.valueType;
  const field = fields.get(operand.fieldKey);
  return field && isNumericFieldType(field.type)
    ? (field.type as CalculationOperandValueType)
    : undefined;
}

function combineType(
  left: CalculationOperandValueType,
  right: CalculationOperandValueType,
  operator: CalculationOperator,
): CalculationOperandValueType | undefined {
  if (operator === 'add' || operator === 'subtract')
    return left === right ? left : undefined;
  if (operator === 'multiply') {
    if (left === 'number') return right;
    if (right === 'number') return left;
    if (left === 'money' && right === 'percent') return 'money';
    if (left === 'percent' && right === 'money') return 'money';
    return undefined;
  }
  if (left === 'number' && right === 'number') return 'number';
  if (left === 'money' && (right === 'number' || right === 'percent'))
    return 'money';
  if (left === 'percent' && right === 'number') return 'percent';
  return undefined;
}

function validateArithmeticTypes(
  operands: CalculationOperand[],
  operators: CalculationOperator[],
  fields: Map<string, CalculationFieldDescriptor>,
  expectedResult: CalculationResultType,
): string[] {
  const errors: string[] = [];
  const types = operands.map((operand) => typeOfOperand(operand, fields));
  if (types.some((type) => type === undefined)) {
    errors.push('计算因子的类型无法确定');
    return errors;
  }
  // The definition has no parentheses. Validation follows the same standard
  // precedence as evaluation: multiply/divide first, then add/subtract.
  const groups: CalculationOperandValueType[] = [
    types[0] as CalculationOperandValueType,
  ];
  const groupOperators: CalculationOperator[] = [];
  for (let index = 0; index < operators.length; index += 1) {
    const operator = operators[index];
    const right = types[index + 1] as CalculationOperandValueType;
    if (operator === 'multiply' || operator === 'divide') {
      const next = combineType(groups[groups.length - 1], right, operator);
      if (!next) errors.push('相乘或相除的字段单位不兼容');
      else groups[groups.length - 1] = next;
    } else {
      groupOperators.push(operator);
      groups.push(right);
    }
  }
  let resultType = groups[0];
  for (let index = 0; index < groupOperators.length; index += 1) {
    const next = combineType(
      resultType,
      groups[index + 1],
      groupOperators[index],
    );
    if (!next) errors.push('相加或相减的字段单位必须一致');
    else resultType = next;
  }
  if (resultType !== expectedResult)
    errors.push(`计算结果类型不匹配，应为${expectedResult}`);
  return errors;
}

/** Validate the persisted structured formula, including dependency ownership,
 * type and cycle constraints. The same function is used by repository, UI
 * and the field-value port before any write. */
export function validateCalculationDefinition(
  definition: CalculationDefinition,
  context: CalculationValidationContext,
): CalculationValidationResult {
  const errors: string[] = [];
  const dependencies: string[] = [];
  if (!definition.calculationRef.trim()) errors.push('缺少计算标识');
  if (!definition.definitionVersion.trim()) errors.push('缺少计算定义版本');
  if (!CALCULATION_RESULT_TYPES.includes(definition.resultType))
    errors.push('计算结果类型必须为数字、金额或百分比');
  if (!Array.isArray(definition.operands) || definition.operands.length === 0)
    errors.push('计算至少需要一个因子');
  if (
    !Array.isArray(definition.operators) ||
    definition.operators.length !== Math.max(0, definition.operands.length - 1)
  )
    errors.push('计算因子与运算符数量不匹配');
  if (
    definition.operators.some(
      (operator) => !CALCULATION_OPERATORS.includes(operator),
    )
  )
    errors.push('仅支持加、减、乘、除');

  const fields = new Map(context.fields.map((field) => [field.key, field]));
  const current = context.currentFieldKey;
  for (const operand of definition.operands ?? []) {
    if (operand.kind === 'constant') {
      if (!validConstant(operand)) errors.push('常量值或常量类型无效');
      continue;
    }
    if (operand.kind !== 'field' || !operand.fieldKey.trim()) {
      errors.push('计算因子必须绑定字段');
      continue;
    }
    dependencies.push(operand.fieldKey);
    const field = fields.get(operand.fieldKey);
    if (!field) {
      // Approved system resolver inputs are relationship facts, not
      // enterprise definitions. They are validated by the resolver itself.
      if (!definition.resolverRef)
        errors.push(`依赖字段不存在：${operand.fieldKey}`);
      continue;
    }
    if (current && field.key === current) errors.push('计算字段不能依赖自身');
    if (!isNumericFieldType(field.type))
      errors.push(`依赖字段不是数字类型：${field.label ?? field.key}`);
    if (field.enabled === false)
      errors.push(`依赖字段已停用：${field.label ?? field.key}`);
    if (field.owner !== definition.owner && !context.allowSystemCrossOwner)
      errors.push('计算字段不能跨对象引用');
    if (
      definition.owner !== 'quote-line' &&
      field.factorApplicability === 'relation-scalar'
    )
      errors.push(`实体计算字段不能引用关系标量：${field.label ?? field.key}`);
  }
  if (!definition.resolverRef)
    errors.push(
      ...validateArithmeticTypes(
        definition.operands ?? [],
        definition.operators ?? [],
        fields,
        definition.resultType,
      ),
    );

  return {
    valid: errors.length === 0,
    errors,
    dependencies: [...new Set(dependencies)],
  };
}

export function assertValidCalculationDefinition(
  definition: CalculationDefinition,
  context: CalculationValidationContext,
): void {
  const result = validateCalculationDefinition(definition, context);
  if (!result.valid)
    throw new Error(`计算定义无效：${result.errors.join('；')}`);
}

/** Detect a dependency cycle across calculated fields before persistence. */
export function hasCalculationCycle(
  definitions: readonly CalculationFieldDescriptor[],
): boolean {
  const graph = new Map<string, string[]>();
  for (const field of definitions) {
    graph.set(
      field.key,
      field.calculation?.operands
        .filter(
          (
            operand,
          ): operand is Extract<CalculationOperand, { kind: 'field' }> =>
            operand.kind === 'field',
        )
        .map((operand) => operand.fieldKey) ?? [],
    );
  }
  const visiting = new Set<string>();
  const visited = new Set<string>();
  const visit = (key: string): boolean => {
    if (visiting.has(key)) return true;
    if (visited.has(key)) return false;
    visiting.add(key);
    for (const dependency of graph.get(key) ?? []) {
      if (visit(dependency)) return true;
    }
    visiting.delete(key);
    visited.add(key);
    return false;
  };
  return definitions.some((field) => visit(field.key));
}

function toRuntimeValue(
  raw: unknown,
  fallbackType: CalculationOperandValueType,
  fieldKey: string,
): TypedCalculationValue | undefined {
  if (
    raw &&
    typeof raw === 'object' &&
    'value' in raw &&
    'type' in raw &&
    typeof (raw as { value: unknown }).value === 'number' &&
    Number.isFinite((raw as { value: number }).value) &&
    ['number', 'money', 'percent'].includes(
      String((raw as { type: unknown }).type),
    )
  ) {
    const typed = raw as TypedCalculationValue;
    if (
      !isRuntimeValueValid(
        typed.value,
        typed.type,
        DISCOUNT_PERCENT_FIELD_KEYS.has(fieldKey),
      )
    )
      return undefined;
    return typed;
  }
  return typeof raw === 'number' &&
    isRuntimeValueValid(
      raw,
      fallbackType,
      DISCOUNT_PERCENT_FIELD_KEYS.has(fieldKey),
    )
    ? { value: raw, type: fallbackType }
    : undefined;
}

function isRuntimeValueValid(
  value: number,
  type: unknown,
  allowDiscountPercentAbove100 = false,
): type is CalculationOperandValueType {
  if (!CALCULATION_RESULT_TYPES.includes(type as CalculationResultType))
    return false;
  if (!Number.isFinite(value)) return false;
  if (type === 'money') return value >= 0;
  if (type === 'percent')
    return value >= 0 && (allowDiscountPercentAbove100 || value <= 100);
  return true;
}

function readRuntimeOperand(
  operand: CalculationOperand,
  values: Readonly<Record<string, unknown>>,
  context?: CalculationEvaluationContext,
): TypedCalculationValue | undefined {
  if (operand.kind === 'constant')
    return isRuntimeValueValid(operand.value, operand.valueType)
      ? { value: operand.value, type: operand.valueType }
      : undefined;
  return toRuntimeValue(
    values[operand.fieldKey],
    context?.fieldTypes?.[operand.fieldKey] ?? 'number',
    operand.fieldKey,
  );
}

function internalValue(value: TypedCalculationValue): number {
  return value.type === 'percent' ? value.value / 100 : value.value;
}

function externalValue(
  value: number,
  type: CalculationOperandValueType,
): number {
  return type === 'percent' ? value * 100 : value;
}

function evaluateArithmetic(
  definition: CalculationDefinition,
  values: Readonly<Record<string, unknown>>,
  context?: CalculationEvaluationContext,
): CalculationEvaluationResult {
  if (
    !Array.isArray(definition.operands) ||
    definition.operands.length === 0 ||
    !Array.isArray(definition.operators) ||
    definition.operators.length !== definition.operands.length - 1 ||
    definition.operators.some(
      (operator) => !CALCULATION_OPERATORS.includes(operator),
    ) ||
    !CALCULATION_RESULT_TYPES.includes(definition.resultType)
  ) {
    return { ok: false, error: '计算定义结构无效' };
  }
  const inferredFieldTypes: Record<string, CalculationOperandValueType> = {};
  for (const operand of definition.operands) {
    if (operand.kind === 'field')
      inferredFieldTypes[operand.fieldKey] =
        context?.fieldTypes?.[operand.fieldKey] ?? definition.resultType;
  }
  const effectiveContext: CalculationEvaluationContext = {
    ...context,
    fieldTypes: { ...inferredFieldTypes, ...context?.fieldTypes },
  };
  const runtime = definition.operands.map((operand) =>
    readRuntimeOperand(operand, values, effectiveContext),
  );
  const missingFieldKeys = definition.operands
    .map((operand, index) =>
      operand.kind === 'field' && !runtime[index]
        ? operand.fieldKey
        : undefined,
    )
    .filter((key): key is string => Boolean(key));
  if (
    definition.operands.some(
      (operand, index) => operand.kind === 'constant' && !runtime[index],
    )
  )
    return { ok: false, error: '常量值或常量类型无效' };
  if (missingFieldKeys.length)
    return {
      ok: false,
      missingFieldKeys: [...new Set(missingFieldKeys)],
      error: '依赖字段缺失或不是有限数字',
    };

  const resolvedRuntime = runtime.filter(
    (value): value is TypedCalculationValue => value !== undefined,
  );
  const valuesInternal = resolvedRuntime.map(internalValue);
  const types = resolvedRuntime.map((value) => value.type);
  const terms: number[] = [valuesInternal[0]];
  const termTypes: CalculationOperandValueType[] = [types[0]];
  const lowOperators: CalculationOperator[] = [];
  for (let index = 0; index < definition.operators.length; index += 1) {
    const operator = definition.operators[index];
    const right = valuesInternal[index + 1];
    const rightType = types[index + 1];
    if (operator === 'multiply' || operator === 'divide') {
      if (operator === 'divide' && right === 0)
        return { ok: false, error: '除数不能为零' };
      const nextType = combineType(
        termTypes[termTypes.length - 1],
        rightType,
        operator,
      );
      if (!nextType) return { ok: false, error: '计算因子单位不兼容' };
      termTypes[termTypes.length - 1] = nextType;
      terms[terms.length - 1] =
        operator === 'multiply'
          ? terms[terms.length - 1] * right
          : terms[terms.length - 1] / right;
    } else {
      lowOperators.push(operator);
      terms.push(right);
      termTypes.push(rightType);
    }
    if (!Number.isFinite(terms[terms.length - 1]))
      return { ok: false, error: '计算结果不是有限数字' };
  }
  let result = terms[0];
  let resultType = termTypes[0];
  for (let index = 0; index < lowOperators.length; index += 1) {
    const operator = lowOperators[index];
    const next = combineType(resultType, termTypes[index + 1], operator);
    if (!next) return { ok: false, error: '计算因子单位不兼容' };
    result =
      operator === 'add'
        ? result + terms[index + 1]
        : result - terms[index + 1];
    resultType = next;
    if (!Number.isFinite(result))
      return { ok: false, error: '计算结果不是有限数字' };
  }
  if (resultType !== definition.resultType)
    return {
      ok: false,
      error: `计算结果类型不匹配，应为${definition.resultType}`,
    };
  const output = externalValue(result, resultType);
  if (definition.resultType === 'money' && output < 0)
    return { ok: false, error: '金额结果不能为负数' };
  if (definition.resultType === 'percent' && (output < 0 || output > 100))
    return { ok: false, error: '百分比结果必须在0到100之间' };
  return { ok: true, value: output };
}

function finiteNonNegative(value: number | null | undefined): value is number {
  return value != null && Number.isFinite(value) && value >= 0;
}

function validQuantity(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value > 0;
}

function validPercent(value: number | undefined): value is number {
  return value !== undefined && Number.isFinite(value) && value >= 0;
}

function systemMoneyResult(value: number): CalculationEvaluationResult {
  return Number.isFinite(value) &&
    value >= 0 &&
    Number.isSafeInteger(Math.round(value * 100))
    ? { ok: true, value }
    : {
        ok: false,
        error: '系统计算结果必须是大于等于 0 的有限金额且可精确表示为安全分',
      };
}

function halfUpDiscountedMoney(
  amount: number,
  discount: number,
  quantity = 1,
): number {
  const amountCents = Math.round(amount * 100);
  const discountBasisPoints = Math.round(discount * 100);
  const numerator =
    BigInt(amountCents) * BigInt(quantity) * BigInt(discountBasisPoints);
  const cents = (numerator + 5_000n) / 10_000n;
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) return Number.POSITIVE_INFINITY;
  return Number(cents) / 100;
}

/** Canonical implementations for the seven approved system calculations. */
export function evaluateSystemCalculation(
  ref: string,
  context: SystemCalculationContext,
): CalculationEvaluationResult {
  const missing = (keys: string[], error = '系统计算因子缺失') => ({
    ok: false as const,
    missingFieldKeys: keys,
    error,
  });
  const moneyTimesQuantity = (
    moneyKey: string,
    money: number | null | undefined,
    quantity: number | undefined,
  ) => {
    if (!finiteNonNegative(money)) return missing([moneyKey]);
    if (!validQuantity(quantity))
      return missing(['quantity'], '数量必须是大于 0 的整数');
    return systemMoneyResult(money * quantity);
  };
  if (ref === SYSTEM_CALCULATION_REFS.rentalSubtotal)
    return moneyTimesQuantity(
      'rental_price',
      context.rentalPrice,
      context.quantity,
    );
  if (ref === SYSTEM_CALCULATION_REFS.unitPriceSubtotal)
    return moneyTimesQuantity(
      'device_unit_price',
      context.deviceUnitPrice,
      context.quantity,
    );
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice) {
    if (!finiteNonNegative(context.packageItemPrice))
      return missing(['package_item_price']);
    if (!validPercent(context.itemDiscount)) return missing(['item_discount']);
    return systemMoneyResult(
      context.packageItemPrice * (context.itemDiscount / 100),
    );
  }
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal) {
    const unit = evaluateSystemCalculation(
      LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice,
      context,
    );
    if (!unit.ok) return unit;
    if (!validQuantity(context.quantity)) return missing(['quantity']);
    return systemMoneyResult((unit.value ?? 0) * context.quantity);
  }
  if (
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal
  ) {
    if (!finiteNonNegative(context.packageItemPrice))
      return missing(['package_item_price']);
    if (!validPercent(context.planDiscount))
      return missing(['scheme_discount'], '折扣必须是大于等于 0 的有限数值');
    if (!validPercent(context.itemDiscount)) return missing(['item_discount']);
    const quantity =
      ref === SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal
        ? context.quantity
        : 1;
    if (!validQuantity(quantity))
      return missing(['quantity'], '数量必须是大于 0 的整数');
    const planUnit = halfUpDiscountedMoney(
      context.packageItemPrice,
      context.planDiscount,
    );
    return systemMoneyResult(
      halfUpDiscountedMoney(planUnit, context.itemDiscount, quantity),
    );
  }
  if (ref === SYSTEM_CALCULATION_REFS.dynamicCostSubtotal)
    return moneyTimesQuantity(
      'dynamic_cost',
      context.dynamicCost,
      context.quantity,
    );
  if (ref === SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal)
    return moneyTimesQuantity(
      'package_item_price',
      context.packageItemPrice,
      context.quantity,
    );
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductPrice) {
    if (!finiteNonNegative(context.deviceUnitPrice))
      return missing(['device_unit_price']);
    if (!validPercent(context.planDiscount))
      return missing(['scheme_discount'], '折扣必须是大于等于 0 的有限数值');
    return systemMoneyResult(
      context.deviceUnitPrice * (context.planDiscount / 100),
    );
  }
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductSubtotal) {
    const price = evaluateSystemCalculation(
      LEGACY_SYSTEM_CALCULATION_REFS.planProductPrice,
      context,
    );
    if (!price.ok) return price;
    if (!validQuantity(context.quantity))
      return missing(['package_relation_quantity']);
    if (price.value === undefined) return missing(['scheme_product_price']);
    return systemMoneyResult(price.value * context.quantity);
  }
  if (ref === SYSTEM_CALCULATION_REFS.planProductPrice) {
    if (!finiteNonNegative(context.packageItemPrice))
      return missing(['package_item_price']);
    if (!validPercent(context.planDiscount))
      return missing(['scheme_discount'], '折扣必须是大于等于 0 的有限数值');
    return systemMoneyResult(
      halfUpDiscountedMoney(context.packageItemPrice, context.planDiscount),
    );
  }
  if (ref === SYSTEM_CALCULATION_REFS.planProductSubtotal) {
    const price = evaluateSystemCalculation(
      SYSTEM_CALCULATION_REFS.planProductPrice,
      context,
    );
    if (!price.ok) return price;
    if (!validQuantity(context.quantity))
      return missing(['package_relation_quantity']);
    if (price.value === undefined) return missing(['scheme_product_price']);
    return systemMoneyResult(price.value * context.quantity);
  }
  if (ref === SYSTEM_CALCULATION_REFS.planPackagePrice) {
    if (!validPercent(context.planDiscount))
      return missing(['scheme_discount'], '折扣必须是大于等于 0 的有限数值');
    if (!Array.isArray(context.packageRelations))
      return missing(['package_relations']);
    let value = 0;
    for (const relation of context.packageRelations) {
      const line = finiteNonNegative(relation.packageItemPrice)
        ? systemMoneyResult(
            relation.packageItemPrice * (context.planDiscount / 100),
          )
        : missing(['package_item_price']);
      const quantity = relation.quantity;
      if (!line.ok) return line;
      if (!validQuantity(quantity))
        return missing(['package_relation_quantity']);
      if (line.value === undefined) return missing(['scheme_product_price']);
      value += line.value * quantity;
      if (!Number.isFinite(value)) return systemMoneyResult(value);
    }
    return systemMoneyResult(value);
  }
  if (ref === SYSTEM_CALCULATION_REFS.packageOriginalPrice) {
    if (!Array.isArray(context.packageRelations))
      return missing(['package_relations']);
    let value = 0;
    for (const relation of context.packageRelations) {
      if (!finiteNonNegative(relation.packageItemPrice))
        return missing(['package_item_price']);
      if (!validQuantity(relation.quantity))
        return missing(['package_relation_quantity']);
      value += relation.packageItemPrice * relation.quantity;
      if (!Number.isFinite(value)) return systemMoneyResult(value);
    }
    return systemMoneyResult(value);
  }
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine) {
    if (!finiteNonNegative(context.currentPricingBase))
      return missing(['current_pricing_base']);
    if (!validPercent(context.planDiscount))
      return missing(['scheme_discount'], '折扣必须是大于等于 0 的有限数值');
    if (!validPercent(context.itemDiscount))
      return missing(['item_discount'], '折扣必须是大于等于 0 的有限数值');
    if (!validQuantity(context.quantity))
      return missing(['quantity'], '数量必须是大于 0 的整数');
    return systemMoneyResult(
      context.currentPricingBase *
        (context.planDiscount / 100) *
        (context.itemDiscount / 100) *
        context.quantity,
    );
  }
  if (ref === SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine) {
    if (!finiteNonNegative(context.currentPricingBase))
      return missing(['current_pricing_base']);
    if (!validPercent(context.planDiscount))
      return missing(['scheme_discount'], '折扣必须是大于等于 0 的有限数值');
    if (!validPercent(context.itemDiscount))
      return missing(['item_discount'], '折扣必须是大于等于 0 的有限数值');
    if (!validQuantity(context.quantity))
      return missing(['quantity'], '数量必须是大于 0 的整数');
    const planUnit = halfUpDiscountedMoney(
      context.currentPricingBase,
      context.planDiscount,
    );
    return systemMoneyResult(
      halfUpDiscountedMoney(planUnit, context.itemDiscount, context.quantity),
    );
  }
  if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedQuote ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedQuote
  ) {
    if (!Array.isArray(context.pricedLines)) return missing(['package_lines']);
    let value = 0;
    for (const line of context.pricedLines) {
      const result = evaluateSystemCalculation(
        ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedQuote
          ? LEGACY_SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine
          : SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine,
        {
          currentPricingBase: line.pricingBaseAmount,
          planDiscount: line.planDiscount ?? 100,
          itemDiscount: line.itemDiscount,
          quantity: line.quantity,
        },
      );
      if (!result.ok) return result;
      if (result.value === undefined)
        return missing(['product_final_discount_price']);
      value += result.value;
      if (!Number.isFinite(value)) return systemMoneyResult(value);
    }
    return systemMoneyResult(value);
  }
  return { ok: false, error: `未知系统计算：${ref}` };
}

function auditedFactor(
  context: SystemCalculationContext,
  fieldKey: string,
  value: number | null | undefined,
  valueType: CalculationOperandValueType,
  identity: { itemId?: string; relationId?: string } = {},
): FrozenCalculationFactorFact | undefined {
  const audit = context.audit;
  if (!audit || value == null || !Number.isFinite(value)) return undefined;
  return {
    fieldKey,
    value,
    valueType,
    quoteId: audit.quoteId,
    quoteVersion: audit.quoteVersion,
    owner: audit.owner,
    scopeId: audit.scopeId,
    ...((identity.itemId ?? audit.itemId)
      ? { itemId: identity.itemId ?? audit.itemId }
      : {}),
    ...((identity.relationId ?? audit.relationId)
      ? { relationId: identity.relationId ?? audit.relationId }
      : {}),
  };
}

/** Evaluate an approved system calculation and capture the exact typed input
 * facts from that same context. Aggregate members retain their stable item /
 * relation identities so the frozen result can be replayed without catalog
 * access. */
export function evaluateSystemCalculationWithFactors(
  ref: string,
  context: SystemCalculationContext,
): CalculationEvaluationWithFactorsResult {
  const evaluated = evaluateSystemCalculation(ref, context);
  if (!evaluated.ok) return evaluated;
  if (!context.audit) return { ok: false, error: '系统计算审计上下文缺失' };
  const direct = (
    entries: readonly [
      string,
      number | null | undefined,
      CalculationOperandValueType,
    ][],
  ) => {
    const facts = entries.map(([key, value, type]) =>
      auditedFactor(context, key, value, type),
    );
    return facts.every(Boolean)
      ? (facts as FrozenCalculationFactorFact[])
      : undefined;
  };
  let factorFacts: FrozenCalculationFactorFact[] | undefined;
  if (ref === SYSTEM_CALCULATION_REFS.rentalSubtotal)
    factorFacts = direct([
      ['rental_price', context.rentalPrice, 'money'],
      ['quantity', context.quantity, 'number'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.unitPriceSubtotal)
    factorFacts = direct([
      ['device_unit_price', context.deviceUnitPrice, 'money'],
      ['quantity', context.quantity, 'number'],
    ]);
  else if (ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['item_discount', context.itemDiscount, 'percent'],
    ]);
  else if (ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['item_discount', context.itemDiscount, 'percent'],
      ['quantity', context.quantity, 'number'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
      ['item_discount', context.itemDiscount, 'percent'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
      ['item_discount', context.itemDiscount, 'percent'],
      ['quantity', context.quantity, 'number'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.dynamicCostSubtotal)
    factorFacts = direct([
      ['dynamic_cost', context.dynamicCost, 'money'],
      ['quantity', context.quantity, 'number'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['package_relation_quantity', context.quantity, 'number'],
    ]);
  else if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductPrice)
    factorFacts = direct([
      ['device_unit_price', context.deviceUnitPrice, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
    ]);
  else if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductSubtotal)
    factorFacts = direct([
      ['device_unit_price', context.deviceUnitPrice, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
      ['package_relation_quantity', context.quantity, 'number'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.planProductPrice)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
    ]);
  else if (ref === SYSTEM_CALCULATION_REFS.planProductSubtotal)
    factorFacts = direct([
      ['package_item_price', context.packageItemPrice, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
      ['package_relation_quantity', context.quantity, 'number'],
    ]);
  else if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine ||
    ref === SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine
  )
    factorFacts = direct([
      ['current_pricing_base', context.currentPricingBase, 'money'],
      ['scheme_discount', context.planDiscount, 'percent'],
      ['item_discount', context.itemDiscount, 'percent'],
      ['quantity', context.quantity, 'number'],
    ]);
  else if (
    ref === SYSTEM_CALCULATION_REFS.planPackagePrice ||
    ref === SYSTEM_CALCULATION_REFS.packageOriginalPrice
  ) {
    factorFacts = [];
    for (const relation of context.packageRelations ?? []) {
      if (!relation.relationId)
        return { ok: false, error: '套餐聚合关系身份缺失' };
      if (context.audit.owner === 'quote-line' && !relation.itemId)
        return { ok: false, error: '报价套餐聚合行身份缺失' };
      const identity = {
        relationId: relation.relationId,
        itemId: relation.itemId,
      };
      const facts = [
        auditedFactor(
          context,
          'package_item_price',
          relation.packageItemPrice,
          'money',
          identity,
        ),
        auditedFactor(
          context,
          'package_relation_quantity',
          relation.quantity,
          'number',
          identity,
        ),
        ...(ref === SYSTEM_CALCULATION_REFS.planPackagePrice
          ? [
              auditedFactor(
                context,
                'scheme_discount',
                context.planDiscount,
                'percent',
                identity,
              ),
            ]
          : []),
      ];
      if (facts.some((fact) => !fact))
        return { ok: false, error: '套餐聚合审计因子缺失' };
      factorFacts.push(...(facts as FrozenCalculationFactorFact[]));
    }
  } else if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedQuote ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedQuote
  ) {
    factorFacts = [];
    for (const line of context.pricedLines ?? []) {
      if (!line.itemId) return { ok: false, error: '套餐报价行身份缺失' };
      const identity = { itemId: line.itemId, relationId: line.relationId };
      const facts = [
        auditedFactor(
          context,
          'current_pricing_base',
          line.pricingBaseAmount,
          'money',
          identity,
        ),
        auditedFactor(
          context,
          'scheme_discount',
          line.planDiscount ?? 100,
          'percent',
          identity,
        ),
        auditedFactor(
          context,
          'item_discount',
          line.itemDiscount,
          'percent',
          identity,
        ),
        auditedFactor(context, 'quantity', line.quantity, 'number', identity),
      ];
      if (facts.some((fact) => !fact))
        return { ok: false, error: '套餐报价行审计因子缺失' };
      factorFacts.push(...(facts as FrozenCalculationFactorFact[]));
    }
  }
  if (!factorFacts) return { ok: false, error: '系统计算审计因子定义缺失' };
  return { ...evaluated, factorFacts };
}

function oneFactor(
  facts: readonly FrozenCalculationFactorFact[],
  fieldKey: string,
): FrozenCalculationFactorFact | undefined {
  const matches = facts.filter((fact) => fact.fieldKey === fieldKey);
  return matches.length === 1 ? matches[0] : undefined;
}

export function calculationAuditFactorKeys(
  definition: CalculationDefinition,
): string[] {
  const ref = definition.resolverRef;
  if (!ref)
    return definition.operands.flatMap((operand) =>
      operand.kind === 'field' ? [operand.fieldKey] : [],
    );
  if (ref === SYSTEM_CALCULATION_REFS.rentalSubtotal)
    return ['rental_price', 'quantity'];
  if (ref === SYSTEM_CALCULATION_REFS.unitPriceSubtotal)
    return ['device_unit_price', 'quantity'];
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice)
    return ['package_item_price', 'item_discount'];
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal)
    return ['package_item_price', 'item_discount', 'quantity'];
  if (ref === SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice)
    return ['package_item_price', 'scheme_discount', 'item_discount'];
  if (ref === SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal)
    return [
      'package_item_price',
      'scheme_discount',
      'item_discount',
      'quantity',
    ];
  if (ref === SYSTEM_CALCULATION_REFS.dynamicCostSubtotal)
    return ['dynamic_cost', 'quantity'];
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductPrice)
    return ['device_unit_price', 'scheme_discount'];
  if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductSubtotal)
    return [
      'device_unit_price',
      'scheme_discount',
      'package_relation_quantity',
    ];
  if (ref === SYSTEM_CALCULATION_REFS.planProductPrice)
    return ['package_item_price', 'scheme_discount'];
  if (ref === SYSTEM_CALCULATION_REFS.planProductSubtotal)
    return [
      'package_item_price',
      'scheme_discount',
      'package_relation_quantity',
    ];
  if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine ||
    ref === SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine
  )
    return [
      'current_pricing_base',
      'scheme_discount',
      'item_discount',
      'quantity',
    ];
  if (ref === SYSTEM_CALCULATION_REFS.planPackagePrice)
    return [
      'package_item_price',
      'package_relation_quantity',
      'scheme_discount',
    ];
  if (ref === SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal)
    return ['package_item_price', 'package_relation_quantity'];
  if (ref === SYSTEM_CALCULATION_REFS.packageOriginalPrice)
    return ['package_item_price', 'package_relation_quantity'];
  if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedQuote ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedQuote
  )
    return [
      'current_pricing_base',
      'scheme_discount',
      'item_discount',
      'quantity',
    ];
  return [];
}

/** Replay a frozen result using only its persisted definition and factor
 * facts. No live catalog or resolver is consulted. */
export function replayCalculationFromFactors(
  definition: CalculationDefinition,
  factors: readonly FrozenCalculationFactorFact[],
  fieldTypes: Readonly<Record<string, CalculationOperandValueType>> = {},
): CalculationEvaluationResult {
  if (!definition.resolverRef) {
    const values: Record<string, number> = {};
    for (const operand of definition.operands) {
      if (operand.kind !== 'field') continue;
      const fact = oneFactor(factors, operand.fieldKey);
      if (!fact)
        return { ok: false, error: `冻结因子身份不唯一：${operand.fieldKey}` };
      values[operand.fieldKey] = fact.value;
    }
    return evaluateCalculation(definition, values, { fieldTypes });
  }
  const value = (key: string) => oneFactor(factors, key)?.value;
  const context: SystemCalculationContext = {};
  const ref = definition.resolverRef;
  if (ref === SYSTEM_CALCULATION_REFS.rentalSubtotal) {
    context.rentalPrice = value('rental_price');
    context.quantity = value('quantity');
  } else if (ref === SYSTEM_CALCULATION_REFS.unitPriceSubtotal) {
    context.deviceUnitPrice = value('device_unit_price');
    context.quantity = value('quantity');
  } else if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice
  ) {
    context.packageItemPrice = value('package_item_price');
    if (ref === SYSTEM_CALCULATION_REFS.packageDiscountedUnitPrice)
      context.planDiscount = value('scheme_discount');
    context.itemDiscount = value('item_discount');
  } else if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal
  ) {
    context.packageItemPrice = value('package_item_price');
    if (ref === SYSTEM_CALCULATION_REFS.packageDiscountedSubtotal)
      context.planDiscount = value('scheme_discount');
    context.itemDiscount = value('item_discount');
    context.quantity = value('quantity');
  } else if (ref === SYSTEM_CALCULATION_REFS.dynamicCostSubtotal) {
    context.dynamicCost = value('dynamic_cost');
    context.quantity = value('quantity');
  } else if (ref === SYSTEM_CALCULATION_REFS.packageItemMonthlySubtotal) {
    context.packageItemPrice = value('package_item_price');
    context.quantity = value('package_relation_quantity');
  } else if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductPrice) {
    context.deviceUnitPrice = value('device_unit_price');
    context.planDiscount = value('scheme_discount');
  } else if (ref === LEGACY_SYSTEM_CALCULATION_REFS.planProductSubtotal) {
    context.deviceUnitPrice = value('device_unit_price');
    context.planDiscount = value('scheme_discount');
    context.quantity = value('package_relation_quantity');
  } else if (ref === SYSTEM_CALCULATION_REFS.planProductPrice) {
    context.packageItemPrice = value('package_item_price');
    context.planDiscount = value('scheme_discount');
  } else if (ref === SYSTEM_CALCULATION_REFS.planProductSubtotal) {
    context.packageItemPrice = value('package_item_price');
    context.planDiscount = value('scheme_discount');
    context.quantity = value('package_relation_quantity');
  } else if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine ||
    ref === SYSTEM_CALCULATION_REFS.finalDiscountedRelationLine
  ) {
    context.currentPricingBase = value('current_pricing_base');
    context.planDiscount = value('scheme_discount');
    context.itemDiscount = value('item_discount');
    context.quantity = value('quantity');
  } else if (
    ref === SYSTEM_CALCULATION_REFS.planPackagePrice ||
    ref === SYSTEM_CALCULATION_REFS.packageOriginalPrice
  ) {
    const relationIds = [...new Set(factors.map((fact) => fact.relationId))];
    if (relationIds.some((id) => !id))
      return { ok: false, error: '冻结套餐关系身份缺失' };
    context.packageRelations = relationIds.map((relationId) => {
      const scoped = factors.filter((fact) => fact.relationId === relationId);
      return {
        relationId,
        itemId: scoped.find((fact) => fact.itemId)?.itemId,
        packageItemPrice: oneFactor(scoped, 'package_item_price')?.value ?? NaN,
        quantity: oneFactor(scoped, 'package_relation_quantity')?.value ?? NaN,
      };
    });
    if (ref === SYSTEM_CALCULATION_REFS.planPackagePrice) {
      const discounts = factors.filter(
        (fact) => fact.fieldKey === 'scheme_discount',
      );
      if (
        discounts.length !== relationIds.length ||
        new Set(discounts.map((fact) => fact.value)).size !== 1
      )
        return { ok: false, error: '冻结方案折扣事实不一致' };
      context.planDiscount = discounts[0]?.value;
    }
  } else if (
    ref === LEGACY_SYSTEM_CALCULATION_REFS.packageDiscountedQuote ||
    ref === SYSTEM_CALCULATION_REFS.packageDiscountedQuote
  ) {
    const itemIds = [...new Set(factors.map((fact) => fact.itemId))];
    if (itemIds.some((id) => !id))
      return { ok: false, error: '冻结套餐报价行身份缺失' };
    context.pricedLines = itemIds.map((itemId) => {
      const scoped = factors.filter((fact) => fact.itemId === itemId);
      return {
        itemId,
        relationId: scoped.find((fact) => fact.relationId)?.relationId,
        pricingBaseAmount:
          oneFactor(scoped, 'current_pricing_base')?.value ?? NaN,
        planDiscount: oneFactor(scoped, 'scheme_discount')?.value,
        itemDiscount: oneFactor(scoped, 'item_discount')?.value ?? NaN,
        quantity: oneFactor(scoped, 'quantity')?.value ?? NaN,
      };
    });
  } else return { ok: false, error: `未知系统计算：${ref}` };
  return evaluateSystemCalculation(ref, context);
}

/** Evaluate a persisted definition. System definitions always go through the
 * typed resolver above; all other definitions use the same precedence/type
 * semantics for UI preview, object pages, snapshots and channel projection. */
export function evaluateCalculation(
  definition: CalculationDefinition,
  values: Readonly<Record<string, unknown>>,
  context?: CalculationEvaluationContext,
): CalculationEvaluationResult {
  if (definition.resolverRef) {
    if (!context?.system) return { ok: false, error: '系统计算缺少受控上下文' };
    return evaluateSystemCalculation(definition.resolverRef, context.system);
  }
  return evaluateArithmetic(definition, values, context);
}

export function formatCalculationDefinition(
  definition: CalculationDefinition,
  labels: Readonly<Record<string, string>> = {},
): string {
  if (definition.resolverRef) {
    const canonical = systemCalculationByRef(definition.resolverRef);
    if (canonical) {
      const displayName =
        labels[canonical.resultFieldKey] ??
        labels[canonical.calculationRef] ??
        canonical.displayName;
      return `${displayName} = ${canonical.displayFormula}`;
    }
  }
  return definition.operands
    .map((operand, index) => {
      const text =
        operand.kind === 'field'
          ? (labels[operand.fieldKey] ?? operand.fieldKey)
          : operand.valueType === 'percent'
            ? `${operand.value}%`
            : operand.valueType === 'money'
              ? `¥${operand.value}`
              : String(operand.value);
      return index === 0
        ? text
        : `${OPERATOR_LABELS[definition.operators[index - 1]]} ${text}`;
    })
    .join(' ');
}

export function systemCalculationByRef(
  ref: string,
): SystemCalculationDefinition | undefined {
  const definition = SYSTEM_CALCULATION_DEFINITIONS.find(
    (candidate) => candidate.calculationRef === ref,
  );
  return definition ? structuredClone(definition) : undefined;
}
